/** Wording for the Visitors Desk. Counts sit next to rates; a missing number is a dash. */
import type { Compared, Meta, Verdict } from '../api/visitorsDesk'
import type { DeskCompare } from './deskPeriod'
import { fmtDate, fmtInt, fmtRelative } from './format'

export type Tone = 'up' | 'down' | 'flat'

const DASH = '—'
const MINUS = '−'

const pctText = (fraction: number) => `${Math.round(fraction * 100)}%`

/** The change against the previous window. Counts show a percentage (a count
 *  with a base under 30 shows the plain change), rates show percentage points,
 *  and `unit: 'positions'` shows a plain number (Google position). */
export function changeText(c: Compared, unit?: 'positions'): { text: string; tone: Tone } {
  const flat = { text: DASH, tone: 'flat' as Tone }
  if (c.change === null || c.change === undefined) return flat
  const dir = (n: number): Tone => (n > 0 ? 'up' : n < 0 ? 'down' : 'flat')
  const arrow = (t: Tone) => (t === 'up' ? '▲' : '▼')
  if (c.points) {
    const n = unit === 'positions' ? Math.round(c.change * 10) / 10 : Math.round(c.change * 100)
    const tone = dir(n)
    if (tone === 'flat') return { text: unit === 'positions' ? '0' : '0 pts', tone }
    const mag = Math.abs(n)
    return { text: unit === 'positions' ? `${arrow(tone)} ${mag}` : `${arrow(tone)} ${mag} pts`, tone }
  }
  if (c.change_pct !== null && c.change_pct !== undefined) {
    const n = Math.round(c.change_pct * 100)
    const tone = dir(n)
    return tone === 'flat' ? { text: '0%', tone } : { text: `${arrow(tone)} ${Math.abs(n)}%`, tone }
  }
  const tone = dir(c.change)
  if (tone === 'flat') return { text: '0', tone }
  return { text: `${tone === 'up' ? '+' : MINUS}${fmtInt(Math.abs(c.change))}`, tone }
}

/** Last year's value, marked `(GA4)` when it comes from Google Analytics. */
export function lastYearText(c: Compared): string {
  if (c.last_year === null || c.last_year === undefined) return `${DASH} (no data yet)`
  const v = c.points ? pctText(c.last_year) : fmtInt(Math.round(c.last_year))
  return c.last_year_source === 'ga4' ? `${v} (GA4)` : v
}

const VERDICTS: Record<Verdict, string> = {
  normal: 'Inside normal range', above: 'Above normal range', below: 'Below normal range', collecting: 'Collecting',
}

export function verdictText(v: Verdict | null | undefined): string {
  return v ? VERDICTS[v] : ''
}

/** `41% (1,310)`; the count stays when the rate is hidden for a small base. */
export function rateWithCount(rate: number | null | undefined, count: number | null | undefined): string {
  const n = typeof count === 'number' && !Number.isInteger(count) ? fmtCredit(count) : fmtInt(count)
  return `${rate === null || rate === undefined ? DASH : pctText(rate)} (${n})`
}

/** The API already merges ISO and name forms (`California (US-CA)`); blanks read Unknown. */
export function regionText(label: string | null | undefined): string {
  return label && label.trim() ? label.trim() : 'Unknown'
}

/** Site-search result counts come from the API as text (`4+` for a capped topic count). */
export function resultsText(display: string | null | undefined): string {
  return display && display.trim() ? display : DASH
}

const NAMES: Record<string, string> = {
  flight_journal: 'Flight Journal', model_airplane_news: 'Model Airplane News',
  rc_car_action: 'RC Car Action', diecast: 'Diecast X',
}

export function pubLabel(slug: string): string {
  return slug === 'all' ? 'All titles' : NAMES[slug] ?? slug
}

/** "Includes Flight Journal, Model Airplane News. Not yet: RC Car Action, Diecast X." */
export function includedText(meta: Pick<Meta, 'titles_included' | 'titles_excluded'>): string {
  const inc = meta.titles_included.map(pubLabel)
  const exc = meta.titles_excluded.map((e) => pubLabel(e.publication))
  const first = inc.length ? `Includes ${inc.join(', ')}.` : 'Includes no titles yet.'
  return exc.length ? `${first} Not yet: ${exc.join(', ')}.` : first
}

/** The frame's bot chip. A missing count (bots unavailable) reads just "Humans only". */
export function botChipText(hidden: number | null | undefined): string {
  return hidden === null || hidden === undefined ? 'Humans only' : `Humans only · ${fmtInt(hidden)} bot visits hidden`
}

/** A calendar day (YYYY-MM-DD) from the API, shown as that day in every browser time zone.
 *  fmtDate alone reads a bare date as midnight UTC, the evening before in the US. */
export function fmtDay(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }): string {
  if (!iso) return DASH
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? fmtDate(`${iso}T12:00:00Z`, { ...opts, timeZone: 'UTC' }) : fmtDate(iso, opts)
}

/** The header of a "change" column: the window it is measured against. */
export function compareHeader(compare: DeskCompare): string {
  return compare === 'last_year' ? 'vs last year' : 'vs prev'
}

/** The frame's data-freshness line. */
export function freshnessText(f: { rollups_through: string | null; last_event_at: string | null; unsessioned: number }): string {
  const through = f.rollups_through ? fmtDay(f.rollups_through) : 'none yet'
  return `Data through ${through} · last event ${fmtRelative(f.last_event_at)} · ${fmtInt(f.unsessioned)} events waiting to be grouped`
}

/** Members gained is shared across the articles a person read, so it can be fractional: whole numbers plain, else one decimal. */
export function fmtCredit(n: number | null | undefined): string {
  if (n === null || n === undefined) return DASH
  if (n > 0 && n < 0.05) return '<0.1'   // a real fraction of a credit would otherwise read 0.0
  return Number.isInteger(n) ? fmtInt(n) : n.toFixed(1)
}

/** Members gained is credited overnight, so a live (today) view shows a dash. */
export function creditText(n: number | null | undefined, live: boolean): string {
  return live ? DASH : fmtCredit(n)
}

export const CREDIT_TODAY_NOTE = 'Members gained: credited overnight, so today shows a dash.'
